
import loader from '../partials/loader'
import purchaseHistory from '../purchaseHistory'

const pricingHandler = (productGeneric) => ({
    ...loader,
    productGeneric,
    productId: -1,
    minimum: 1,
    maximum: 10000000,
    maximumExceeded: false,
    quantity: 1,
    selectedIndex: -1,
    qtyChanged: false,
    basePriceFormatted: 'Pricing Not Available',
    currentPriceFormatted: 'Pricing Not Available',
    minimumQuantityPriceFormatted: 'Pricing Not Available',
    minimumQuantityPriceFormattedExTax: 'Pricing Not Available',
    currentSavingPercentage: '',
    maxSavingPercentage: '',
    priceBreaks: [],
    nextPriceBreak: null,
    showNextPriceBreak: false,
    thresholdMap: {},
    basePrice: null,
    dateDiscontinued: null,
    isAvailableForOrder: null,
    manufacturerLead: null,
    express_cutoff: 0,
    express_quantity: 0,
    buttonHeld: false,
    heldTimout: null,
    productHistory: null,
    total_formatted: '',
    stockAndPrice: null,
    limitedStockLevel: 0,
    init() {
        this.selectedIndex = -1
        if (typeof productGeneric === 'object') {
            this.assignProductData(productGeneric)
            return
        }

        if (productGeneric) {
            this.productId = productGeneric
            window.api.getPricesAndStock([{ id: this.productId, quantity: 1, index: 0 }]).then((result) => {
                this.assignProductData(result[this.productId].data)
            })
        }
    },
    reloadStock() {
        if (this.productId) {
            window.api.getPricesAndStock([{ id: this.productId, quantity: 1, index: 0 }]).then((result) => {
                this.assignProductData(result[this.productId].data)
            })
        }
    },
    priceRange() {
        if (this.priceBreaks.length === 0 || this.priceBreaks.length === 1) {
            return this.basePriceFormatted
        }

        return `${this.priceBreaks[this.priceBreaks.length - 1].price_formatted} - ${this.basePriceFormatted}`
    },
    calculateNextBreak() {
        // Quantity and a selected price break required
        if (!this.quantity || this.selectedIndex < 0) {
            this.nextPriceBreak = null
            this.showNextPriceBreak = false
            return
        }
        const higherBreaks = this.priceBreaks.filter((priceBreak) => priceBreak.from_quantity > this.quantity)
        this.nextPriceBreak = higherBreaks.length > 0 ? higherBreaks[0] : null
        if (this.nextPriceBreak) {
            const distanceToNextBreak = this.nextPriceBreak.from_quantity - this.quantity
            // Show if less than 10 qty or 10% away
            this.showNextPriceBreak = distanceToNextBreak <= 10 || distanceToNextBreak <= (this.nextPriceBreak.from_quantity * 0.1)
        } else {
            this.showNextPriceBreak = false
        }
    },
    assignProductData(productData) {
        this.productId = productData.id_product
        this.basePrice = productData.price
        this.dateDiscontinued = productData.date_discontinued ?? null
        this.isAvailableForOrder = productData.available_for_order === 1
        this.basePriceFormatted = productData.price_formatted
        this.minimumQuantityPriceFormatted = this.isUKGoogleShopping() ? productData.price_formatted_minimum_quantity_taxed : productData.price_formatted_minimum_quantity
        this.express_cutoff = productData.express_cutoff ? productData.express_cutoff : 0
        this.minimumQuantityPriceFormattedExTax = productData.price_formatted_minimum_quantity
        this.limitedStockLevel = productData.limited_stock_level

        if (productData.price_breaks) {
            productData.price_breaks.unshift(
                {
                    from_quantity: productData.minimal_quantity,
                    // The base tier needs a numeric price like every other
                    // tier: add_to_cart reads priceBreaks[selectedIndex].price
                    price: productData.price,
                    price_formatted: productData.price_formatted,
                    price_formatted_taxed: productData.price_formatted_taxed,
                    saving_percentage: 0,
                },
            )
        }

        this.priceBreaks = this.sortPriceBreaks(productData.price_breaks ?? [])
        this.manufacturerLead = productData.ex_lead_time
        this.pricesReady = true
        this.minimum = productData.minimal_quantity
        this.quantity = productData.minimal_quantity
        if (productData.limited_stock_level) {
            this.maximum = Math.max(productData.quantity, productData.ex_stock, productData.secondary_stock)
        }

        this.express_quantity = productData.quantity
        if (this.priceBreaks.length > 0) {
            this.maxSavingPercentage = this.priceBreaks[this.priceBreaks.length - 1].saving_percentage
        }

        /**
         * Build a map where the key is the minimum quantity for each price break
         * and the value is the object key within this.priceBreaks
         */
        this.priceBreaks.forEach((priceBreak, index) => {
            this.thresholdMap[priceBreak.from_quantity] = index
        })

        purchaseHistory.getProductHistory(productData.id_product).then((data) => { this.productHistory = data })

        this.stockAndPrice = productData
        window.dispatchEvent(new CustomEvent('pricing-loaded', { detail: productData }))

        this.calculateTotal()
        this.calculateNextBreak()

        this.stopLoader()
    },
    updateStock($event) {
        if ($event.detail.id_product !== this.productId) {
            return
        }

        this.stockAndPrice = $event.detail
    },
    changeQuantity(amount = 1) {
        let iterations = 0
        clearTimeout(this.heldTimout)
        this.heldTimout = null

        const checkButtonHeld = () => {
            this.quantity = Math.max(this.minimum, Math.min(this.maximum, this.quantity += amount))
            this.reactToQuantityUpdate()

            const delay = 1500 / (iterations + 3)
            iterations += 1

            this.heldTimout = setTimeout(() => {
                if (this.buttonHeld) {
                    checkButtonHeld()
                }
            }, delay)
        }

        checkButtonHeld()
    },
    selectPriceBreak(breakIndex, ignoreReselection = false) {
        window.Alpine.store('pricing').quantityChanged = true

        // Set our current index for this.PriceBreak
        this.selectedIndex = (this.selectedIndex === breakIndex && !ignoreReselection) ? -1 : breakIndex

        // Decide if the base price or a break price should be displayed
        this.currentPriceFormatted = breakIndex !== -1 ? this.priceBreaks[breakIndex].price_formatted : this.basePriceFormatted
        this.currentSavingPercentage = breakIndex !== -1 ? this.priceBreaks[breakIndex].saving_percentage : ''
    },
    reactToQuantityUpdate() {
        window.Alpine.store('pricing').quantityChanged = true
        this.qtyChanged = true

        if (this.quantity !== null) {
            this.maximumExceeded = this.quantity > this.maximum
            this.quantity = Math.round(Math.max(1, Math.min(this.quantity, this.maximum)))

            if (this.quantity.toString().length > 6) {
                this.quantity = parseInt(this.quantity.toString().substring(0, 6))
            }
        }

        if (Object.keys(this.thresholdMap).length <= 0) {
            return
        }

        /**
         * Use our map keys to decide where the current quantity
         * falls within our break point list.
         */
        const calculatedIndex = Object.keys(this.thresholdMap).reduce(
            (previous, current) => (
                (Math.abs(current - this.quantity) < Math.abs(previous - this.quantity))
                && current <= this.quantity
                    ? current
                    : previous
            ),
        )

        /**
         * If the current quantity isn't less than the minimum
         * and our quantity isn't less than the smallest break point
         * then select a break point and update the UI.
         *
         * Otherwise default to no break point selected.
         */
        this.selectPriceBreak(
            (this.quantity < this.minimum || this.quantity < Object.keys(this.thresholdMap)[0])
                ? -1
                : this.thresholdMap[calculatedIndex],
            true,
        )

        this.calculateTotal()
        this.calculateNextBreak()
    },
    calculateTotal() {
        // Can't calculate without stock and price data
        if (!this.stockAndPrice || !this.stockAndPrice.currency_iso_code) return

        const formatter = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: this.stockAndPrice.currency_iso_code,
        })

        this.total_formatted = formatter.format((this.priceBreaks[this.selectedIndex]?.price || this.stockAndPrice.price) * this.quantity)
    },
    sortPriceBreaks(priceBreaks) {
        return priceBreaks.sort((alpha, bravo) => (alpha.from_quantity > bravo.from_quantity ? 1 : -1))
    },
    isGoogleShopping() {
        return new URLSearchParams(window.location.search).get('uk_google_shopping') !== null || new URLSearchParams(window.location.search).get('google_shopping') !== null
    },
    isUKGoogleShopping() {
        return new URLSearchParams(window.location.search).get('uk_google_shopping') !== null
    },
    showMinimumReachedError(amount = 1) {
        window.dispatchEvent(new CustomEvent('create-toast', {
            detail: {
                title: 'Invalid Quantity',
                message: `The minimum quantity for this product is ${amount}`,
                type: 'error',
            },
        }))
    },
    getMinutes(minutes) {
        if (minutes < 0) return 0
        return Math.floor(minutes - (Math.floor(minutes / 60) * 60))
    },
    getHours(minutes) {
        if (minutes < 0) return 0
        return Math.floor(minutes / 60)
    },
})

document.addEventListener('alpine:init', () => {
    window.Alpine.data('pricingHandler', pricingHandler)
})

export default pricingHandler
